// stores/useSearchStore.ts
import { ref, onMounted, nextTick } from "vue";
import { defineStore } from "pinia";
import { liteClient as algoliasearch } from "algoliasearch/lite";
import axios from "axios";
import { addAlgoliaInsightsMetadata } from "@/js/utils/algoliaInsights";

export const useSearchStore = defineStore("search", () => {
    const query = ref("");
    const selectedTopic = ref("");
    const selectedResourceType = ref("");
    const selectedVideoType = ref("");
    const results = ref<any[]>([]);
    const autocompleteResults = ref<any[]>([]);
    const total = ref(0);
    const totalPages = ref(0);
    const page = ref(1);
    const showAutocomplete = ref(false);
    const searchTopics = ref<any[]>([]);
    const searchResourceTypes = ref<any[]>([]);
    const searchVideoTypes = ref<any[]>([]);
    const isLoading = ref(false);
    const hasFetchedFacets = ref(false);
    const isAdvancedVideoSearch = () =>
        typeof window !== "undefined" &&
        window.location.pathname.includes("/video-library/advanced-search");
    const isSearchPage = ref<boolean>(
        typeof window !== "undefined" &&
            (window.location.pathname.includes("/search") ||
                isAdvancedVideoSearch())
    );

    const RESULTS_PER_PAGE = 12;

    const appId = import.meta.env.VITE_ALGOLIA_APPLICATION_ID;
    const apiKey = import.meta.env.VITE_ALGOLIA_SEARCH_API_KEY;
    const searchClient = algoliasearch(appId, apiKey);
    const indexName = import.meta.env.VITE_ALGOLIA_INDEX_PREFIX + "ALL_CONTENT";

    const isUnfilteredVideoLibrarySearch = () =>
        typeof window !== "undefined" &&
        window.location.pathname.includes("/video-library/advanced-search") &&
        selectedResourceType.value === "Video" &&
        !query.value?.trim() &&
        !selectedTopic.value &&
        !selectedVideoType.value;

    const updateURL = (params: Record<string, string | number | string[]>) => {
        if (!isSearchPage.value) return;

        const queryString = new URLSearchParams();
        const pageNumber = Number(params.page ?? 1);
        const isAdvancedVideoSearch =
            window.location.pathname.includes("/video-library/advanced-search");

        if (params.q) queryString.set("q", String(params.q));
        if (params.topic) queryString.set("topic", String(params.topic));
        if (params.resourceType && !isAdvancedVideoSearch) {
            queryString.set("resourceType", String(params.resourceType));
        }
        if (params.videoType) queryString.set("videoType", String(params.videoType));
        if (pageNumber > 1) queryString.set("page", String(pageNumber));

        const nextSearch = queryString.toString();
        const newUrl = nextSearch
            ? `${window.location.pathname}?${nextSearch}`
            : window.location.pathname;

        if (window.location.search !== (nextSearch ? `?${nextSearch}` : "")) {
            window.history.replaceState({}, "", newUrl);
        }
    };

    const populateFromURL = () => {
        if (!isSearchPage.value) return;

        const params = new URLSearchParams(window.location.search);
        query.value = params.get("q") || "";
        selectedTopic.value = params.get("topic") || "";
        selectedResourceType.value =
            params.get("resourceType") || (isAdvancedVideoSearch() ? "Video" : "");
        selectedVideoType.value = params.get("videoType") || "";
        page.value = parseInt(params.get("page") || "1");
    };

    const fetchSearchResults = async () => {
        if (
            !query.value.trim() &&
            !selectedTopic.value &&
            !selectedResourceType.value &&
            !selectedVideoType.value
        ) {
            results.value = [];
            total.value = 0;
            totalPages.value = 0;
            console.warn("⚠️ No search query or filters provided.");
            return;
        }

        isLoading.value = true;

        try {
            let facetFilters: string[][] = [];

            if (selectedTopic.value) {
                facetFilters.push([`relatedTopics:${selectedTopic.value}`]);
            }

            if (selectedResourceType.value) {
                facetFilters.push([
                    `relatedResourceTypes:${selectedResourceType.value}`,
                ]);
            }

            if (selectedVideoType.value) {
                facetFilters.push([`videoTypesSlugs:${selectedVideoType.value}`]);
            }

            const isFilterOnly =
                !query.value?.trim() &&
                (
                    selectedTopic.value ||
                    selectedResourceType.value ||
                    selectedVideoType.value
                );

            const response = await searchClient.search([
                {
                    indexName,
                    query: query.value || "",
                    params: {
                        hitsPerPage: 1000,
                        facetFilters: facetFilters.length
                            ? facetFilters
                            : undefined,
                        getRankingInfo: true,
                        clickAnalytics: true,
                        analyticsTags: isFilterOnly
                            ? ["filter-only"]
                            : ["typed-search"],
                    },
                },
            ]);
            const queryID = response.results[0]?.queryID;
            const allResults = response.results[0]?.hits || [];

            if (isUnfilteredVideoLibrarySearch()) {
                allResults.sort(
                    (a, b) =>
                        (Number(b.postDate) || 0) -
                        (Number(a.postDate) || 0)
                );
            } else {
                allResults.sort(
                    (a, b) =>
                        (b._rankingInfo?.nbExactWords || 0) -
                        (a._rankingInfo?.nbExactWords || 0)
                );
            }
            const resultsWithInsights = addAlgoliaInsightsMetadata(
                allResults,
                queryID
            );

            total.value = resultsWithInsights.length;
            totalPages.value = Math.ceil(total.value / RESULTS_PER_PAGE);
            results.value = resultsWithInsights.slice(
                (page.value - 1) * RESULTS_PER_PAGE,
                page.value * RESULTS_PER_PAGE
            );

            updateURL({
                q: query.value,
                topic: selectedTopic.value,
                resourceType: selectedResourceType.value,
                videoType: selectedVideoType.value,
                page: page.value,
            });
        } catch (error) {
            console.error("🚨 Error fetching search results:", error);
        } finally {
            isLoading.value = false;
        }
    };

    let lastQuery = "";
    const fetchAutocompleteResults = async () => {
        if (
            !query.value ||
            query.value.length < 3 ||
            query.value === lastQuery
        ) {
            showAutocomplete.value = false;
            autocompleteResults.value = [];
            return;
        }

        lastQuery = query.value;

        try {
            let facetFilters: string[][] = [];

            if (selectedTopic.value) {
                facetFilters.push([`relatedTopics:${selectedTopic.value}`]);
            }

            if (selectedResourceType.value) {
                facetFilters.push([
                    `relatedResourceTypes:${selectedResourceType.value}`,
                ]);
            }

            if (selectedVideoType.value) {
                facetFilters.push([`videoTypesSlugs:${selectedVideoType.value}`]);
            }

            const response = await searchClient.search([
                {
                    indexName,
                    query: query.value,
                    params: {
                        hitsPerPage: 10, // grab extra to allow sorting/slicing
                        getRankingInfo: true,
                        clickAnalytics: true,
                        attributesToRetrieve: [
                            "title",
                            "displayTitle",
                            "url",
                            "teaser",
                            "autoDescription",
                            "copy",
                            "sectionHandle",
                            "relatedTopics",
                            "relatedResourceTypes",
                            "videoTypes",
                            "videoTypesSlugs",
                            "videoCollections",
                            "videoCollectionsSlugs",
                            "firstName",
                            "lastName",
                            "photoUrl",
                            "slug",
                            "type",
                            "isAuthor",
                            "fullName",
                        ],
                        facetFilters: facetFilters.length
                            ? facetFilters
                            : undefined,
                    },
                },
            ]);

            const queryID = response.results[0]?.queryID;
            const hits = response.results[0]?.hits || [];

            // Optional: sort by title relevance or other criteria
            hits.sort(
                (a, b) =>
                    (b._rankingInfo?.nbExactWords || 0) -
                    (a._rankingInfo?.nbExactWords || 0)
            );

            autocompleteResults.value = addAlgoliaInsightsMetadata(
                hits,
                queryID
            ).slice(0, 5);
            showAutocomplete.value = autocompleteResults.value.length > 0;

            await nextTick();
        } catch (error) {
            console.error("❌ Error fetching autocomplete results:", error);
            autocompleteResults.value = [];
            showAutocomplete.value = false;
        }
    };

    const fetchFacets = async () => {
        hasFetchedFacets.value = false;

        try {
            const response = await axios.get(
                "/actions/readinguniverse/algolia/get-facets-from-craft"
            );
            searchTopics.value = response.data.topicsFacets || [];
            searchResourceTypes.value = response.data.resourceFacets || [];
            searchVideoTypes.value = response.data.videoTypes || [];
        } catch (error) {
            console.error("🚨 Error fetching facets:", error);
            searchTopics.value = [];
            searchResourceTypes.value = [];
            searchVideoTypes.value = [];
        } finally {
            hasFetchedFacets.value = true;
        }
    };

    onMounted(() => {
        populateFromURL();
        fetchFacets();
    });

    return {
        query,
        selectedTopic,
        selectedResourceType,
        selectedVideoType,
        results,
        autocompleteResults,
        total,
        totalPages,
        page,
        isLoading,
        hasFetchedFacets,
        showAutocomplete,
        searchTopics,
        searchResourceTypes,
        searchVideoTypes,
        fetchSearchResults,
        fetchAutocompleteResults,
        fetchFacets,
        updateURL,
        populateFromURL,
    };
});
